import { createEntityAdapter, createSelector, createSlice, type EntityState, type PayloadAction } from '@reduxjs/toolkit'
import { type RootState } from '../store'
import { type CrossClientAction, CrossClientActionPhases } from '../../models/CrossClient'

interface CrossClientActionsState {
  actions: EntityState<CrossClientAction, string>
  pollingEnabled: boolean
  lastActionUpdatedAt: string | null
  lastManualTriggerAt: number | null
}

const crossClientActionsAdapter = createEntityAdapter<CrossClientAction>()

const initialState: CrossClientActionsState = {
  actions: crossClientActionsAdapter.getInitialState(),
  pollingEnabled: true,
  lastActionUpdatedAt: null,
  lastManualTriggerAt: null,
}

export const crossClientActionsSlice = createSlice({
  name: 'crossClientActions',
  initialState,
  reducers: {
    setCrossClientActions: (state, action: PayloadAction<CrossClientAction[]>) => {
      if (!action.payload.length) {
        return
      }
      let maxUpdatedAt = state.lastActionUpdatedAt ?? ''
      for (const item of action.payload) {
        const cursor = item.finishedAt ?? item.startedAt
        if (cursor && cursor > maxUpdatedAt) {
          maxUpdatedAt = cursor
        }
      }
      crossClientActionsAdapter.upsertMany(state.actions, action.payload)
      if (maxUpdatedAt) {
        state.lastActionUpdatedAt = maxUpdatedAt
      }
    },
    startCrossClientActionsPolling: (state) => {
      state.pollingEnabled = true
    },
    stopCrossClientActionsPolling: (state) => {
      state.pollingEnabled = false
    },
    triggerCrossClientActionsPolling: (state, action: PayloadAction<number>) => {
      state.pollingEnabled = true
      state.lastManualTriggerAt = action.payload
    },
    resetCrossClientActions: () => initialState,
  },
})

export const {
  setCrossClientActions,
  startCrossClientActionsPolling,
  stopCrossClientActionsPolling,
  triggerCrossClientActionsPolling,
  resetCrossClientActions,
} = crossClientActionsSlice.actions

const crossClientActionSelectors = crossClientActionsAdapter.getSelectors((state: RootState) => state.crossClientActions.actions)

export const selectCrossClientActions = (state: RootState) => crossClientActionSelectors.selectAll(state)

export const selectCrossClientActionsPollingEnabled = (state: RootState) => state.crossClientActions.pollingEnabled

export const selectLastCrossClientActionUpdatedAt = (state: RootState) => state.crossClientActions.lastActionUpdatedAt

export const selectLastCrossClientActionsManualTriggerAt = (state: RootState) => state.crossClientActions.lastManualTriggerAt

export const selectCrossClientActionsByClientId = createSelector([selectCrossClientActions], (actions) => {
  return actions.reduce(
    (map, action) => {
      const list = map[action.clientId] ?? []
      list.push(action)
      map[action.clientId] = list
      return map
    },
    {} as Record<string, CrossClientAction[]>,
  )
})

export const selectCrossClientActionsHasInProgress = createSelector([selectCrossClientActions], (actions) =>
  actions.some((a) => a.phase === CrossClientActionPhases.STARTED),
)

export default crossClientActionsSlice.reducer
